/**
 * Company-Research — Evidence Drawer (citation verification) contract — E07-F05.
 *
 * The Review screen's evidence-drawer shows the proof base behind one scored
 * long-list row: per the prototype `research.proto.js` (openEvidence L1095,
 * requestMoreInfo L1138, verdictChip L840, gradeLabel L1133) over the v2
 * `research_companies` model (breakdown / evidence / needs_human / grade / score).
 *
 * 1:1 port source: EER app/prototype/assets/research.proto.js + research.data.js
 * (criteriaKeys order, weights, ev() shape). The drawer surfaces — per criterion
 * in the canonical order — the verbatim quote, the source URL, the verdict and
 * the WEIGHTED CONTRIBUTION (weight × match-factor; pass=1.0/partial=0.5/fail=0.0),
 * a trust banner (citation-verified vs needs-human) and the division/portfolio
 * note for extracted-division entities. "Request more info" re-runs a deeper
 * web_search to upgrade the weakest (first PARTIAL else FAIL) criterion.
 *
 * This file is feature-owned. It is re-exported from `@eer/shared` by the serial
 * Wire pass — it MUST NOT edit the shared barrel during parallel work.
 *
 * It depends only on the already-shared primitives from `./research-pipeline.ts`
 * (CriterionVerdict, EvidenceEntry, Grade, SCORE_CRITERIA, VERDICT_MATCH,
 * DEFAULT_WEIGHTS, EntityType) — single source of truth, no re-declaration.
 */
import { z } from 'zod';
import { SCORE_CRITERIA, VERDICT_MATCH, DEFAULT_WEIGHTS, criterionVerdictSchema, gradeSchema, entityTypeSchema, } from './research-pipeline.js';
import { checkStateSchema } from './research-check-state.js';
import { insufficientDataSchema } from './research-insufficient-data.js';
// ---------------------------------------------------------------------------
// Criterion display order + labels (verbatim from research.data.js criteriaKeys)
// ---------------------------------------------------------------------------
/**
 * The canonical render order + labels of the 6 criteria in the drawer
 * (research.data.js `MOCK.criteriaKeys`). The drawer renders criteria in THIS
 * order (industry → subIndustry → geo → size → founded → services) — note the
 * `services` label is "Services match" in the UI.
 */
export const CRITERION_DISPLAY = [
    { key: 'industry', label: 'Industry' },
    { key: 'subIndustry', label: 'Sub-industry' },
    { key: 'geo', label: 'Geo' },
    { key: 'size', label: 'Size' },
    { key: 'founded', label: 'Founded' },
    { key: 'services', label: 'Services match' },
];
/** The criterion keys in drawer render order (industry … services). */
export const CRITERION_ORDER = CRITERION_DISPLAY.map((c) => c.key);
// ---------------------------------------------------------------------------
// Trust banner (T06: verified vs needs-human)
// ---------------------------------------------------------------------------
// E07-F04-T11: 'not-checked' — no AI check / Verify has run; NEUTRAL, never green.
export const TRUST_TONES = ['verified', 'needs-human', 'not-checked'];
export const trustToneSchema = z.enum(TRUST_TONES);
/** Verbatim banner copy from the prototype (openEvidence trust block, L1125-1127). */
export const TRUST_BANNER_VERIFIED = 'Citation-verified. Every PASS/PARTIAL above is backed by a verbatim quote captured from a real source URL — replacing the manual About-Us check.';
export const TRUST_BANNER_NEEDS_HUMAN = 'Needs human review. Confidence below threshold (no/weak first-party evidence). The specialist makes the final call before publish.';
/** E07-F04-T11 — a row nothing has checked yet (raw discovery data). */
export const TRUST_BANNER_NOT_CHECKED = 'Not checked. No AI check or Verify has run for this company yet — the criteria below are unconfirmed discovery data. Run AI check or Verify before publishing.';
/**
 * E07-F11-T05 — the verified banner of a Jev row: Jev keeps no verbatim quotes (spec §5.4 п.4 —
 * the confidence rule replaces citation-or-reject), so the Haiku "verbatim quote" copy would lie.
 */
export const TRUST_BANNER_JEV_VERIFIED = 'Checked by Jev on the full text of the company pages — grade confidence is above the threshold. Open the source pages below to confirm; page fragments are found by search, not quoted by the model.';
export const trustBannerSchema = z.object({
    tone: trustToneSchema,
    message: z.string(),
});
// ---------------------------------------------------------------------------
// Per-criterion evidence row (rendered in the drawer)
// ---------------------------------------------------------------------------
/** E07-F11-T05 — a code-found page fragment (Jev rows): a hint, never a model quote. */
export const evidenceFragmentSchema = z.object({
    text: z.string(),
    url: z.string(),
    label: z.string(),
});
/** E07-F11-T05 — evidence that rests on search-engine snippets, not the company site. */
export const evidenceSearchSourceSchema = z.object({
    label: z.string(),
    urls: z.array(z.string()),
});
/**
 * One rendered evidence row in the drawer. `contribution` is the weighted match
 * (weight × {pass:1.0,partial:0.5,fail:0.0}); `weight` is the active profile's
 * percent for the criterion. `hasQuote`/`hasUrl` drive the amber "no verbatim
 * quote" / "No source URL" fallbacks. `verdictLabel` is the upper-cased verdict
 * shown on the badge (PASS/PARTIAL/FAIL/UNCLEAR).
 *
 * Citation-or-reject at DISPLAY time (E07-F05-T02 AC4): a stored PASS/PARTIAL
 * with no verbatim quote is rendered DOWNGRADED to `unclear` (0 contribution),
 * whatever wrote the row (pipeline, manual add, legacy import) — the same rule
 * `applyVerifyGuard` enforces in the pipeline. `downgradedFrom` keeps the stored
 * (claimed) verdict so the UI can explain the downgrade; null when not downgraded.
 */
export const evidenceCriterionRowSchema = z.object({
    key: z.enum(SCORE_CRITERIA),
    label: z.string(),
    verdict: criterionVerdictSchema.or(z.literal('unclear')),
    /** The stored PASS/PARTIAL that was downgraded for lack of a quote (else null/absent). */
    downgradedFrom: criterionVerdictSchema.nullable().optional(),
    verdictLabel: z.string(),
    quote: z.string(),
    url: z.string(),
    hasQuote: z.boolean(),
    hasUrl: z.boolean(),
    weight: z.number(),
    /** Match factor for the verdict (pass:1.0/partial:0.5/fail:0.0/unclear:0.0). */
    matchFactor: z.number(),
    /** weight × matchFactor — the weighted contribution to the score (T04). */
    contribution: z.number(),
    /**
     * T04 AC3 — for an extracted division / portfolio item, the size/founded rows
     * explain that the UNIT was graded, not the parent (why the verdict is e.g.
     * PARTIAL rather than a parent-driven FAIL). null for every other row.
     */
    unitScopeNote: z.string().nullable().optional(),
    /**
     * E07-F11-T05 — Jev rows only (absent on Haiku rows): the probability Jev gave this
     * criterion's verdict (0..1), null when Jev gave no answer.
     */
    confidence: z.number().nullable().optional(),
    /**
     * E07-F11-T05 — Jev rows only: a page fragment the CODE found around the criterion's key
     * words. A hint, never a model quote — `label` says so («фрагмент найден поиском, не цитата
     * модели»).
     */
    fragment: evidenceFragmentSchema.nullable().optional(),
    /**
     * E07-F11-T05 — Jev rows only: the criterion rests on search-engine snippets, not on the
     * company website (`label` = «по поисковой выдаче, не с сайта компании», `urls` = snippet
     * addresses). null when the criterion rests on the site pages.
     */
    searchSource: evidenceSearchSourceSchema.nullable().optional(),
});
// ---------------------------------------------------------------------------
// Division / portfolio note (T04 — extract-division explainer)
// ---------------------------------------------------------------------------
/** entity_type ≠ pure-play → the amber division/portfolio note + parent name. */
export const divisionNoteSchema = z
    .object({
    entityType: entityTypeSchema,
    parent: z.string().nullable(),
    /** "division of <Parent>" / "portfolio item · <Parent>". */
    label: z.string(),
})
    .nullable();
// ---------------------------------------------------------------------------
// The full drawer DTO (GET response)
// ---------------------------------------------------------------------------
/**
 * E07-F11-T05 — the Jev block of a row verified by Jev (spec §5.4 п.4: «доказательство вместо
 * дословной цитаты»). null/absent on Haiku rows — they render exactly as before.
 */
export const evidenceJevSchema = z.object({
    /** The pages the decision was taken on (each opens in a new tab). */
    pages: z.array(z.string()),
    /** `site` — the pages alone; `search` — snippets alone; `site+search` — both. */
    basis: z.enum(['site', 'search', 'site+search']),
    /** Snippet addresses the verdict also read, labelled `searchLabel` (empty when none). */
    searchUrls: z.array(z.string()),
    /** «по поисковой выдаче, не с сайта компании». */
    searchLabel: z.string(),
    /** Probability Jev gave the grade (or the broken rule); null for a code rule / no answer. */
    gradeConfidence: z.number().nullable(),
});
export const evidenceDrawerSchema = z.object({
    researchCompanyId: z.number().int().positive(),
    name: z.string(),
    /** Subheader: "<resolvedEntity> · <gradeLabel> · score <n>" pieces (T04). */
    resolvedEntity: z.string(),
    gradeLabel: z.string(),
    grade: gradeSchema.nullable(),
    score: z.number().nullable(),
    /**
     * E07-F01-T04 — the one-line, request-tied grade rationale (the same string as the
     * Review row's info-icon tooltip), rendered in the drawer's header subtitle.
     */
    rationale: z.string().nullable().default(null),
    needsHuman: z.boolean(),
    /** E07-F04-T11 — derived check state (drives the banner tone). */
    checkState: checkStateSchema.default('verified'),
    /** E07-F01-T11 — "insufficient data" + its reason (null = the row has evidence / was not checked). */
    insufficientData: insufficientDataSchema.nullable().optional(),
    trust: trustBannerSchema,
    divisionNote: divisionNoteSchema,
    criteria: z.array(evidenceCriterionRowSchema),
    /** Σ(contribution) — sanity-checks against `score` (T04 acceptance #4). */
    contributionTotal: z.number(),
    /** E07-F11-T05 — present only for a row verified by Jev. */
    jev: evidenceJevSchema.nullable().optional(),
});
export const evidenceDrawerResponseSchema = z.object({
    ok: z.literal(true),
    drawer: evidenceDrawerSchema,
});
// ---------------------------------------------------------------------------
// Request more info (T03) — deeper web_search upgrade
// ---------------------------------------------------------------------------
export const requestMoreInfoSchema = z.object({
    researchCompanyId: z.number().int().positive(),
});
/**
 * Result of "Request more info": which criterion was upgraded (null when there
 * was nothing weaker to upgrade), the re-rendered drawer, and a `degraded` flag
 * set when the active adapter could NOT run web_search (OpenAI-fallback) — in
 * that case `upgraded` is null and `needsHuman` stays as-is (no silent success).
 */
/**
 * E07-F05-T07 — the explicit outcome of a "Request more info" run, so the UI
 * never confuses "the paid check found nothing new" with "already verified":
 *   - `upgraded`         — a new cited confirmation was found and persisted;
 *   - `not_found`        — the check ran but returned no citable evidence (row unchanged);
 *   - `already_verified` — every criterion is already PASS (nothing was run);
 *   - `degraded`         — the check could not run / failed (no model, adapter error).
 */
export const REQUEST_MORE_INFO_OUTCOMES = [
    'upgraded',
    'not_found',
    'already_verified',
    'degraded',
];
export const requestMoreInfoOutcomeSchema = z.enum(REQUEST_MORE_INFO_OUTCOMES);
export const requestMoreInfoResponseSchema = z.object({
    ok: z.literal(true),
    /** The criterion key that was upgraded to PASS, or null. */
    upgraded: z.enum(SCORE_CRITERIA).nullable(),
    /** True when no web_search-capable adapter was available → no upgrade. */
    degraded: z.boolean(),
    /** Which of the four outcomes happened (E07-F05-T07). */
    outcome: requestMoreInfoOutcomeSchema,
    drawer: evidenceDrawerSchema,
});
// ---------------------------------------------------------------------------
// Pure helpers (shared by api projection + web rendering)
// ---------------------------------------------------------------------------
/** grade → "Grade A"/"Grade B"/"Grade C" (prototype gradeLabel, L1133). */
export function gradeLabel(grade) {
    if (grade == null)
        return '—';
    const map = { A: 'Grade A', B: 'Grade B', C: 'Grade C' };
    return map[grade] ?? grade;
}
/** Match factor for a verdict — pass:1.0 / partial:0.5 / fail:0.0 / unclear:0.0. */
export function verdictMatchFactor(verdict) {
    if (verdict === 'unclear')
        return 0;
    return VERDICT_MATCH[verdict];
}
/**
 * The trust banner for a row by its needs_human flag (T06). E07-F04-T11: when the
 * derived `checkState` is supplied and is 'not_checked', the banner is the NEUTRAL
 * "Not checked" one — never the green citation-verified copy.
 */
export function buildTrustBanner(needsHuman, checkState) {
    if (!needsHuman && checkState === 'not_checked') {
        return { tone: 'not-checked', message: TRUST_BANNER_NOT_CHECKED };
    }
    return needsHuman
        ? { tone: 'needs-human', message: TRUST_BANNER_NEEDS_HUMAN }
        : { tone: 'verified', message: TRUST_BANNER_VERIFIED };
}
/**
 * The division/portfolio note for a non-pure-play entity (T04). Mirrors the
 * prototype subheader: division-of → "division of <Parent>"; portfolio-item →
 * "portfolio item · <Parent>". Returns null for a pure-play company.
 */
export function buildDivisionNote(entityType, parent) {
    if (entityType === 'division-of') {
        return { entityType: 'division-of', parent, label: `division of ${parent ?? 'parent'}` };
    }
    if (entityType === 'portfolio-item') {
        return {
            entityType: 'portfolio-item',
            parent,
            label: `portfolio item · ${parent ?? 'parent'}`,
        };
    }
    return null;
}
/** The criteria judged on the extracted unit, not the parent (E07-F01-T08 #2/#3). */
export const UNIT_SCOPED_CRITERIA = ['size', 'founded'];
/**
 * T04 AC3 — the per-criterion explainer for an extracted division / portfolio
 * item: size and founded are judged on the UNIT (the scoring stage re-scopes them,
 * researchScoringDivision.ts), so a 5000+ / decades-old parent does not fail them.
 * The verdict-specific tail says why the unit landed on PASS / PARTIAL / FAIL.
 * Returns null for a pure-play, for any other criterion, or when the note does
 * not apply.
 */
export function buildUnitScopeNote(key, verdict, entityType, unit, parent) {
    if (entityType !== 'division-of' && entityType !== 'portfolio-item')
        return null;
    if (!UNIT_SCOPED_CRITERIA.includes(key))
        return null;
    const u = (unit ?? '').trim() || 'the extracted unit';
    const p = (parent ?? '').trim() || 'the parent group';
    if (key === 'size') {
        const base = `Judged on ${u}'s own headcount, not ${p}'s group-wide size.`;
        if (verdict === 'partial')
            return `${base} PARTIAL: the unit, not the parent, was graded — its headcount is only partly inside the size band or is estimated (a division rarely publishes its own headcount).`;
        if (verdict === 'pass')
            return `${base} The parent's size does not count against it.`;
        if (verdict === 'fail')
            return `${base} Even the unit on its own is outside the size band.`;
        return base;
    }
    const base = `Judged on when ${u} itself was founded or launched, not on ${p}'s founding year.`;
    if (verdict === 'partial')
        return `${base} PARTIAL: the unit, not the parent, was graded — its own start date is approximate or only partly inside the window (units often report the parent's date).`;
    if (verdict === 'pass')
        return `${base} An older parent does not count against it.`;
    if (verdict === 'fail')
        return `${base} Even the unit itself is outside the founded window.`;
    return base;
}
export { DEFAULT_WEIGHTS };
//# sourceMappingURL=research-evidence.js.map